import { useMemo, useState } from "react";
import { fDia } from "../../lib/trading";
import "../../styles/trading.css";

export const RANGOS = [["1S", 7], ["1M", 30], ["3M", 91], ["6M", 182], ["1A", 365], ["Todo", 0]];
const lunes = (iso) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const clave = (iso, iv) => (iv === "S" ? lunes(iso) : iv === "M" ? `${iso.slice(0, 7)}-01` : iso);

// Agrega una serie diaria [{fecha, valor}] a D/S/M. modo: last (saldos/tasas), sum (flujos), ohlc (velas).
export const agregar = (pts, iv, modo = "last") => {
  const g = new Map();
  let prev = null;
  for (const p of pts) {
    if (p.valor == null) continue;
    const k = clave(p.fecha, iv);
    const o = g.get(k) || { time: k, open: prev ?? p.valor, high: -Infinity, low: Infinity, close: p.valor, value: 0 };
    o.high = Math.max(o.high, p.valor, o.open); o.low = Math.min(o.low, p.valor, o.open); o.close = p.valor; o.value = modo === "sum" ? o.value + p.valor : p.valor;
    g.set(k, o); prev = p.valor;
  }
  return [...g.values()].map((o) => (modo === "ohlc" ? { time: o.time, open: o.open, high: o.high, low: o.low, close: o.close } : { time: o.time, value: o.value }));
};

export const recortar = (pts, dias) => {
  if (!dias || !pts.length) return pts;
  const fin = new Date(`${pts[pts.length - 1].fecha}T00:00:00Z`);
  fin.setUTCDate(fin.getUTCDate() - dias);
  const lim = fin.toISOString().slice(0, 10);
  return pts.filter((p) => p.fecha >= lim);
};

const Seg = ({ opts, v, set, testid }) => (
  <div className="flex rounded-lg border border-[var(--trd-border)] p-0.5" data-testid={testid}>{opts.map(([k, l]) => (
    <button key={k} type="button" onClick={() => set(k)} data-testid={`${testid}-${l}`} aria-pressed={v === k}
      className={`trd-mono rounded-md px-2 py-0.5 text-[11px] ${v === k ? "bg-[var(--trd-surface-2)] text-white" : "text-[var(--trd-muted)] hover:text-white"}`}>{l}</button>))}</div>
);

export const useRango = (def = 0) => { const [r, setR] = useState(def); return [r, setR]; };

// Panel oscuro estilo trading: encabezado (nombre, último valor, cambio en el rango), rangos, intervalo, gráfica y «¿Cómo se calculó?».
export const TradingPanel = ({ title, ultimo, cambio, tono, rango, setRango, iv, setIv, extra, como, fuente, corte, children, testid, className = "" }) => {
  const cls = tono === "good" ? "trd-good" : tono === "bad" ? "trd-bad" : "trd-flat";
  return (
    <section className={`trd min-w-0 rounded-2xl border border-[var(--trd-border)] p-3 sm:p-4 ${className}`} data-testid={testid}>
      <header className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--trd-muted)]">{title}</p>
          {ultimo != null && <p className="trd-mono text-lg font-semibold sm:text-xl" data-testid={testid ? `${testid}-ultimo` : undefined}>{ultimo}
            {cambio && <span className={`ml-2 text-xs ${cls}`} data-testid={testid ? `${testid}-cambio` : undefined}>{cambio}</span>}</p>}
          {corte && <p className="text-[10px] text-[var(--trd-accent)]" data-testid={testid ? `${testid}-corte` : undefined}>▼ Cartera al corte {fDia(corte, true)} · después la serie se mantiene plana</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {extra}
          {setIv && <Seg opts={[["D", "D"], ["S", "S"], ["M", "M"]]} v={iv} set={setIv} testid={testid ? `${testid}-iv` : "iv"} />}
          {setRango && <Seg opts={RANGOS.map(([l, d]) => [d, l])} v={rango} set={setRango} testid={testid ? `${testid}-rango` : "rango"} />}
        </div>
      </header>
      {children}
      {como && <details className="mt-2 text-[11px] text-[#C9D1D9]" data-testid={testid ? `${testid}-como` : undefined}><summary className="cursor-pointer font-semibold text-[var(--trd-muted)] hover:text-white">¿Cómo se calculó?</summary>
        <p className="mt-1 leading-relaxed">{como}</p>{fuente && <p className="mt-0.5 text-[var(--trd-muted)]">Fuente: {fuente}</p>}</details>}
    </section>
  );
};

export const cambioRango = (pts, fmtDelta) => {
  const v = pts.filter((p) => p.valor != null);
  if (v.length < 2) return { txt: null, dir: "eq" };
  const a = v[0].valor, b = v[v.length - 1].valor, d = b - a;
  return { txt: `${d > 0 ? "▲" : d < 0 ? "▼" : "="} ${fmtDelta(d, a)}`, dir: d > 0 ? "up" : d < 0 ? "down" : "eq" };
};

export const tonoDe = (dir, pol) => (dir === "eq" || pol === "neutral" ? "flat" : (dir === "up") === (pol === "up_good") ? "good" : "bad");

export const useSerie = (pts, rango) => useMemo(() => recortar(pts || [], rango), [pts, rango]);
